import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Radio from '@/components/ui/Radio/Radio';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { PaymentDeviceRentalRequestStatus } from '@/models/common/PaymentDeviceEnums';
import { FundPaymentDevicesDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { formatDeviceDate, fundDevicesManagementUrl } from '@/helpers/paymentDevices';

interface PendingRequestLinkFieldProps {
  /** The fund the device is going to; null while the office has not picked one. */
  fundId: number | null;
  /** The chosen request, or null for «לא לשייך». */
  value: number | null;
  onChange: (requestId: number | null) => void;
}

/**
 * When the office assigns or hands over a device by hand to a fund that is waiting on its own rental
 * request, offers to answer that request with this device - so it moves to «אושר» instead of
 * staying pending. Shows nothing when the fund has no pending request. The oldest is pre-selected.
 */
const PendingRequestLinkField = ({ fundId, value, onChange }: PendingRequestLinkFieldProps) => {
  const { t } = useTranslation();
  const { data } = useGetAuthApi<FundPaymentDevicesDtoIn>(fundId != null ? fundDevicesManagementUrl(fundId) : '');

  const pending = useMemo(
    () => (data?.requests ?? [])
      .filter((r) => r.status === PaymentDeviceRentalRequestStatus.Pending)
      .sort((a, b) => a.submittedAt.localeCompare(b.submittedAt)),
    [data],
  );

  const oldestId = pending[0]?.id ?? null;
  useEffect(() => {
    onChange(oldestId);
    // Re-picked only when the fund (and so its pending list) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oldestId]);

  if (pending.length === 0) return null;

  const option = (id: number | null, label: string) => (
    <label key={id ?? 'none'} className="flex items-center gap-2 cursor-pointer body-sm-regular">
      <Radio name="pd-link-request" checked={value === id} onChange={() => onChange(id)} />
      {label}
    </label>
  );

  return (
    <fieldset className="flex flex-col gap-2 m-0 rounded-lg border border-[#f0c48a] bg-[#fff4e5] px-3.5 py-3">
      <legend className="sr-only">{t('paymentDevices.dialogs.linkRequestTitle')}</legend>
      <p className="m-0 body-sm-semibold text-[#6b3a00]">{t('paymentDevices.dialogs.linkRequestTitle')}</p>
      {pending.map((request) => option(request.id, t('paymentDevices.dialogs.linkRequestOption', {
        id: request.id,
        start: formatDeviceDate(request.startDate),
        end: formatDeviceDate(request.expectedReturnDate),
        area: t(`paymentDevices.pickupArea.${request.pickupArea}`),
      })))}
      {option(null, t('paymentDevices.dialogs.linkRequestNone'))}
    </fieldset>
  );
};

export default PendingRequestLinkField;
